// Story 1.27 — TanStack Query mutation for POST /api/v1/admin/creator-applications/:id/reject.

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";

export type RejectResponse = {
  data: {
    application_id: string;
    user_id: string;
    status: "rejected";
    rejected_at: string;
  };
};

export type RejectError = {
  code: string;
  message: string;
  field?: string;
  current_status?: string;
  status: number;
};

async function rejectApplication(
  applicationId: string,
  reason: string
): Promise<RejectResponse> {
  const response = await fetch(
    `/api/v1/admin/creator-applications/${applicationId}/reject`,
    {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        "X-Idempotency-Key": crypto.randomUUID(),
      },
      body: JSON.stringify({ reason }),
    }
  );
  attachRequestIdToScope(response);
  if (!response.ok) {
    let detail: RejectError = {
      code: "ADMIN_REJECT_FAILED",
      message: `Reject failed (HTTP ${response.status})`,
      status: response.status,
    };
    try {
      const raw = await response.json();
      if (raw?.error) {
        detail = {
          code: raw.error.code,
          message: raw.error.message,
          field: raw.error.field,
          current_status: raw.error.current_status,
          status: response.status,
        };
      }
    } catch {
      // body not JSON
    }
    throw detail;
  }
  return (await response.json()) as RejectResponse;
}

export function useRejectCreatorApplication(applicationId: string) {
  const queryClient = useQueryClient();
  return useMutation<RejectResponse, RejectError, string>({
    mutationFn: (reason: string) => rejectApplication(applicationId, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin-creator-applications"],
      });
      queryClient.invalidateQueries({
        queryKey: ["admin-creator-application-detail", applicationId],
      });
    },
  });
}
